<template>
  <div
    ref="wrapperRef"
    class="task-select-wrapper"
    :class="{ 'is-open': isOpen, 'is-disabled': disabled }"
  >
    <div
      ref="triggerRef"
      class="task-select-trigger"
      :class="{ 'is-disabled': disabled }"
      @click="toggleDropdown"
    >
      <span v-if="selectedTask" class="task-select-value">
        <span class="task-id">#{{ selectedTask.iid }}</span>
        <span class="task-title">{{ selectedTask.title }}</span>
      </span>
      <span v-else class="task-select-placeholder">{{ placeholder }}</span>
      <span class="task-select-arrow">▼</span>
    </div>
    <Teleport to="body">
      <div v-if="isOpen" ref="dropdownRef" class="task-select-dropdown" :style="dropdownStyle">
        <div class="task-select-search">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            :placeholder="searchPlaceholder"
            class="task-select-search-input"
            @input="handleSearchInput"
          />
        </div>
        <div v-if="loading" class="task-select-loading">
          {{ __('Loading...') }}
        </div>
        <div v-else-if="displayTasks.length === 0" class="task-select-no-results">
          {{ searchQuery ? __('No tasks found') : __('Type to search tasks...') }}
        </div>
        <div v-else class="task-select-options">
          <div
            v-for="task in displayTasks"
            :key="task.id"
            class="task-select-option"
            :class="{
              'is-selected': String(selectedValue) === String(task.id),
              'is-disabled': isExcluded(task),
            }"
            @click="!isExcluded(task) && selectTask(task)"
          >
            <span class="task-id">#{{ task.iid }}</span>
            <span class="task-title">{{ task.title }}</span>
            <span v-if="isExcluded(task) && excludedHint" class="task-disabled-hint">{{
              excludedHint
            }}</span>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { __ } from '@/locale'

const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  projectId: { type: [String, Number], required: true },
  placeholder: { type: String, default: 'Select task...' },
  searchPlaceholder: { type: String, default: 'Search tasks...' },
  disabled: { type: Boolean, default: false },
  excludeTaskIds: { type: Array, default: () => [] },
  excludedHint: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const selectedValue = computed(() => props.modelValue)
const wrapperRef = ref(null)
const triggerRef = ref(null)
const dropdownRef = ref(null)
const dropdownStyle = ref({})
const searchInputRef = ref(null)
const searchQuery = ref('')
const debounceTimer = ref(null)
const searchedTasks = ref([])
const selectedTaskData = ref(null)

const SEARCH_TASKS_QUERY = gql`
  query SearchProjectTasks($projectId: ID!, $search: String, $first: Int) {
    project(id: $projectId) {
      id
      tasks(first: $first, search: $search, includeArchived: false) {
        edges {
          node {
            id
            iid
            title
          }
        }
      }
    }
  }
`

const {
  load: searchTasks,
  result: searchResult,
  loading,
} = useLazyQuery(SEARCH_TASKS_QUERY, () => ({
  projectId: String(props.projectId),
  search: /^\d+$/.test(searchQuery.value.trim()) ? null : searchQuery.value.trim() || null,
  first: 50,
}))

watch(searchResult, data => {
  if (data?.project?.tasks?.edges) {
    searchedTasks.value = data.project.tasks.edges.map(e => e.node)
    // If we have a modelValue, try to find and cache the selected task
    if (props.modelValue && !selectedTaskData.value) {
      const found = searchedTasks.value.find(t => String(t.id) === String(props.modelValue))
      if (found) {
        selectedTaskData.value = found
      }
    }
  }
})

const displayTasks = computed(() => {
  const tasks = searchedTasks.value
  const query = searchQuery.value.trim()

  // If query looks like a number, prioritize exact IID match
  if (/^\d+$/.test(query)) {
    const exactIid = query
    return [...tasks].sort((a, b) => {
      const aExact = String(a.iid) === exactIid
      const bExact = String(b.iid) === exactIid
      if (aExact && !bExact) return -1
      if (!aExact && bExact) return 1
      return 0
    })
  }

  return tasks
})

function isExcluded(task) {
  return props.excludeTaskIds.includes(String(task.iid)) || props.excludeTaskIds.includes(task.iid)
}

const selectedTask = computed(() => {
  if (
    selectedValue.value === null ||
    selectedValue.value === undefined ||
    selectedValue.value === ''
  )
    return null
  // Check in searched tasks first
  const found = searchedTasks.value.find(t => String(t.id) === String(selectedValue.value))
  if (found) return found
  // Fall back to cached selected task data
  return selectedTaskData.value
})

function handleSearchInput() {
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
  }

  debounceTimer.value = setTimeout(() => {
    searchTasks()
  }, 300)
}

function updateDropdownPosition() {
  if (!isOpen.value || !triggerRef.value) return

  nextTick(() => {
    if (!triggerRef.value) return

    const rect = triggerRef.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const dropdownMaxHeight = 250
    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top

    const shouldPositionAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow

    dropdownStyle.value = {
      position: 'fixed',
      top: shouldPositionAbove ? `${rect.top - dropdownMaxHeight}px` : `${rect.bottom}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 10000,
      maxHeight: shouldPositionAbove
        ? `${Math.min(dropdownMaxHeight, spaceAbove - 10)}px`
        : `${Math.min(dropdownMaxHeight, spaceBelow - 10)}px`,
    }
  })
}

function toggleDropdown() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    updateDropdownPosition()
    // Load initial tasks
    searchTasks()
    nextTick(() => {
      if (searchInputRef.value) {
        // Save current scroll position
        const scrollY = window.scrollY
        const scrollX = window.scrollX

        // Focus with preventScroll option (modern browsers)
        searchInputRef.value.focus({ preventScroll: true })

        // Restore scroll position if browser doesn't support preventScroll
        requestAnimationFrame(() => {
          if (window.scrollY !== scrollY || window.scrollX !== scrollX) {
            window.scrollTo(scrollX, scrollY)
          }
        })
      }
    })
  } else {
    searchQuery.value = ''
    searchedTasks.value = []
  }
}

function selectTask(task) {
  // Cache selected task data for display
  selectedTaskData.value = task
  emit('update:modelValue', task ? task.id : null)
  isOpen.value = false
  searchQuery.value = ''
}

function handleClickOutside(event) {
  if (!isOpen.value) return
  if (wrapperRef.value && wrapperRef.value.contains(event.target)) return
  if (dropdownRef.value && dropdownRef.value.contains(event.target)) return
  isOpen.value = false
  searchQuery.value = ''
}

function handleScroll() {
  if (isOpen.value) {
    updateDropdownPosition()
  }
}

function handleResize() {
  if (isOpen.value) {
    updateDropdownPosition()
  }
}

watch(isOpen, newValue => {
  if (newValue) {
    updateDropdownPosition()
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)
  } else {
    window.removeEventListener('scroll', handleScroll, true)
    window.removeEventListener('resize', handleResize)
  }
})

// Load task by ID when component mounts with a value
watch(
  () => props.modelValue,
  newValue => {
    if (newValue && !selectedTask.value && !loading.value) {
      // Set search query to the ID to find the task
      searchQuery.value = String(newValue)
      searchTasks()
    }
  },
  { immediate: true }
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // If we have a modelValue but no selectedTask, load it
  if (props.modelValue && !selectedTask.value) {
    searchQuery.value = String(props.modelValue)
    searchTasks()
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleScroll, true)
  window.removeEventListener('resize', handleResize)
  if (debounceTimer.value) {
    clearTimeout(debounceTimer.value)
  }
})
</script>

<style scoped>
.task-select-wrapper {
  position: relative;
  width: 100%;
}

.task-select-trigger {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 38px;
  font-size: 14px;
  transition: border-color 0.2s;
  gap: 8px;
}

.task-select-trigger:hover:not(.is-disabled) {
  border-color: #999;
}

.task-select-trigger.is-disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.task-select-wrapper.is-open .task-select-trigger {
  border-color: #2aabee;
}

.task-select-value {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.task-select-placeholder {
  color: #999;
  flex: 1;
}

.task-select-arrow {
  font-size: 10px;
  color: #666;
  transition: transform 0.2s;
  flex-shrink: 0;
}

.task-select-wrapper.is-open .task-select-arrow {
  transform: rotate(180deg);
}

.task-select-dropdown {
  background: #fff;
  border: 1px solid #2aabee;
  border-radius: 4px;
  max-height: 250px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.task-select-search {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}

.task-select-search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  background: #fff;
  color: #333;
}

.task-select-search-input:focus {
  border-color: #2aabee;
}

.task-select-loading,
.task-select-no-results {
  padding: 12px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.task-select-options {
  max-height: 200px;
  overflow-y: auto;
}

.task-select-option {
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.task-select-option:last-child {
  border-bottom: none;
}

.task-select-option:hover {
  background-color: #f8f9fa;
}

.task-select-option.is-selected {
  background-color: #e3f2fd;
}

.task-select-option.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.task-disabled-hint {
  font-size: 11px;
  color: #999;
  margin-left: auto;
}

.task-id {
  color: #666;
  font-size: 13px;
  flex-shrink: 0;
}

.task-title {
  color: #333;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
